import { Box, Button, Divider, Flex, Radio, RadioGroup, Stack } from "@chakra-ui/react";
import { Typography } from "@reconvert/react-ui-component";
import { useTranslation } from "react-i18next";
import { EmptySlotOption, Platform, Slot } from "../../models";
import SlotHeader from "../SlotHeader/SlotHeader";
import { useAppSelector } from "../../app/hooks";
import { selectAppState } from "../../app/slice/appSlice";
import useConditions from "../../hooks/useConditions";
import { useEffect, useMemo, useState } from "react";
import useUnsavedBar, { PreventActionType, PreventActionTypeObject } from "../../hooks/useUnsavedBar";
import { APP_PLATFORM } from "../../config";
import useVariants from "../../hooks/useVariants";
import PreMadeTemplateModal from "../PreMadeTemplateModal/PreMadeTemplateModal";
import useSlots from "../../hooks/useSlots";
import useIsActiveOnPage, { PagePurpose } from "../../hooks/useIsActiveOnPage";

const EmptyState = ({
  slot,
  hideHeader,
  isLoading,
  onSlotDelete,
}: {
  slot: Slot;
  hideHeader?: boolean;
  isLoading: boolean;
  onSlotDelete: () => void;
}) => {
  const { t } = useTranslation();
  const { activePage } = useAppSelector(selectAppState);
  const { preventAction } = useUnsavedBar();
  const { selectedCondition, handleUpdateSlotConfiguration } = useConditions();
  const { handleAddWidget } = useVariants();
  const { handleCreateSlot, openPreMadeTemplateModal, isPreMadeTemplateSlot } = useSlots();
  const isDisableDeleteSlot = useIsActiveOnPage(PagePurpose.DISABLE_DELETE_SLOT, activePage!);
  const [emptySlotOption, setEmptySlotOption] = useState<EmptySlotOption>("CONTINUE");
  const [showPreMadeTemplateModal, setShowPreMadeTemplateModal] = useState(false);

  const slotConfig = useMemo(() => {
    return selectedCondition?.slotConfig?.find(config => config.slotId === slot.id);
  }, [selectedCondition?.slotConfig, slot.id]);

  const isTemplateSlot = isPreMadeTemplateSlot(slot.slotNo, slot.type);

  useEffect(() => {
    if (slotConfig) {
      setEmptySlotOption(slotConfig.emptySlotOption);
    }
  }, [slotConfig]);

  const handleAddWidgetClick = () => {
    if (APP_PLATFORM === Platform.Shopify) {
      if (isTemplateSlot) {
        handleCreateSlot(slot.slotNo, slot.slotNo, null);
        return;
      }

      const opened = openPreMadeTemplateModal({ slotNo: slot.slotNo, slotType: slot.type });

      if (opened) return;

      setShowPreMadeTemplateModal(true);
    } else {
      const variant = selectedCondition?.variants?.filter(a => a.slotId === slot.id);

      if (variant && variant?.length > 0) {
        handleAddWidget(slot, variant[0].id);
      } else {
        handleAddWidget(slot);
      }
    }
  };

  return (
    <>
      {!hideHeader && <SlotHeader slot={slot} />}
      <Box mx={4}>
        <Box
          sx={{
            display: "flex",
            padding: "20px 60px",
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "center",
            alignSelf: "stretch",
            borderRadius: "8px",
            border: "2px dashed",
            borderColor: "neutrals.400",
          }}
        >
          <Typography maxWidth="100%" variant="body" fontWeight={500} textAlign={"center"} color="neutrals.500">
            {t("When slot is empty:")}
          </Typography>

          <Box mt="8px">
            <RadioGroup
              onChange={value => {
                preventAction(() => {
                  if (slotConfig?.id) {
                    handleUpdateSlotConfiguration(slotConfig?.id, emptySlotOption, value as EmptySlotOption);
                  }
                }, PreventActionTypeObject.INLINE_CHANGE as PreventActionType);
              }}
              value={emptySlotOption}
            >
              <Stack direction="row">
                <Radio value="CONTINUE">
                  <Typography variant="small" fontWeight={400} color="neutrals.900">
                    {t("Continue to the next condition")}
                  </Typography>
                </Radio>
                <Radio value="STOP">
                  <Typography variant="small" fontWeight={400} color="neutrals.900">
                    {t("Keep the slot empty")}
                  </Typography>
                </Radio>
              </Stack>
            </RadioGroup>
          </Box>
          <Divider w="85%" my="14px" />
          <Flex gap="14px">
            <Button
              variant="secondary"
              size={"sm"}
              onClick={() => preventAction(() => handleAddWidgetClick())}
              isLoading={isLoading}
              fontSize={16}
              px={4}
              fontWeight={500}
            >
              {t("Add widgets")}
            </Button>

            {!isDisableDeleteSlot && (
              <Button variant="ghost" size={"sm"} onClick={onSlotDelete} fontSize={16} px={4} fontWeight={500}>
                {t("Delete slot")}
              </Button>
            )}
          </Flex>
        </Box>
        {APP_PLATFORM === Platform.Shopify && (
          <PreMadeTemplateModal
            showPreMadeTemplateModal={showPreMadeTemplateModal}
            onClose={() => setShowPreMadeTemplateModal(false)}
            setShowPreMadeTemplateModal={setShowPreMadeTemplateModal}
            slotNo={slot.slotNo}
            slotType={slot.type}
          />
        )}
      </Box>
    </>
  );
};

export default EmptyState;
